/*
 * The SEO, GEO, AEO tracker's data: types, labels, the calls, and the CSV.
 * Everything comes from the backend (seo.graphqls), which the daily sync keeps in step with the site.
 */
import {
  type SeoKeywordFilterInput,
  type SeoPageFilterInput,
  seoKeywordsQuery,
  seoPageEventsQuery,
  seoPagePerformanceQuery,
  seoPageQuery,
  seoFacetsQuery,
  seoPagesQuery,
  seoRolePagesQuery,
  seoSummaryQuery,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';

import { csvCell } from '../Referrals/payoutExport';

/** What Google's URL Inspection said about a page the last time it was asked. */
export interface SeoGoogleIndex {
  /** PASS, PARTIAL, FAIL or NEUTRAL. */
  verdict: string | null;
  coverageState: string | null;
  indexingState: string | null;
  lastCrawlTime: string | null;
  googleCanonical: string | null;
  inspectedAt: string | null;
}

export interface SeoPage {
  path: string;
  pageType: string | null;
  group: string | null;
  vertical: string | null;
  city: string | null;
  locality: string | null;
  industry: string | null;
  role: string | null;
  primaryKeyword: string | null;
  secondaryKeywords: string[];
  inSitemap: boolean;
  inLlms: boolean;
  indexable: boolean | null;
  liveJobs: number | null;
  lastActivity: string | null;
  httpStatus: number | null;
  title: string | null;
  description: string | null;
  h1: string | null;
  canonical: string | null;
  robots: string | null;
  faqCount: number | null;
  schemaTypes: string[];
  fetchError: string | null;
  status: string | null;
  firstSeenAt: string | null;
  lastChangedAt: string | null;
  lastSyncedAt: string | null;
  noteCount: number;
  googleIndex: SeoGoogleIndex | null;
  clicks28: number | null;
  impressions28: number | null;
  ctr28: number | null;
  position28: number | null;
  clicksPrev28: number | null;
  primaryKeywordPosition: number | null;
  notIndexed: boolean;
  dropping: boolean;
  sessions28: number | null;
  signups28: number | null;
  applies28: number | null;
  aiSessions28: number | null;
  searchSyncedAt: string | null;
  /** Only on the page record and the Categories tab; the Pages list does not ask for it. */
  keywordChecks?: SeoKeywordCheck[];
}

/** Where one target keyword appears on the live page, from the last successful read. */
export interface SeoKeywordCheck {
  keyword: string;
  primary: boolean;
  inTitle: boolean;
  inH1: boolean;
  inDescription: boolean;
  inH2: boolean;
  inBody: boolean;
  onPage: boolean;
}

export type SeoEventKind = 'ADDED' | 'CHANGED' | 'REMOVED' | 'NOTE';

export interface SeoEvent {
  id: string;
  path: string;
  at: string;
  kind: SeoEventKind;
  text: string;
  note: string | null;
  by: string | null;
  changes: { field: string; from: string | null; to: string | null }[];
}

export interface SeoSummary {
  total: number;
  indexable: number;
  inLlms: number;
  withFaq: number;
  changedLast7Days: number;
  lastSyncedAt: string | null;
  byType: { pageType: string | null; count: number }[];
  clicks28: number;
  impressions28: number;
  /** GA4 figures: null when GA4 is not connected. */
  sessions28: number | null;
  signups28: number | null;
  aiSessions28: number | null;
  notIndexed: number;
  dropping: number;
  searchSyncedAt: string | null;
  ga4Connected: boolean;
  /** Where the Google sync stands: the button waits on it, whoever started the run. */
  searchSync: SeoSyncState | null;
  inventorySync: SeoSyncState | null;
}

/**
 * A sync's state, from the backend. The syncs take minutes, so the mutation only starts one and
 * the page polls this. running is false once it finished, failed, or was cut off by a restart.
 */
export interface SeoSyncState {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  /** A sentence for the toast, e.g. "Read 1,040 pages: 3 added, 12 changed, 0 removed." */
  result: string | null;
}

/** Figures for every page matching the filter, not only the screenful. */
export interface SeoPageTotals {
  pages: number;
  clicks28: number;
  impressions28: number;
  sessions28: number;
  signups28: number;
  applies28: number;
  aiSessions28: number;
}

export interface SeoFacet {
  value: string;
  count: number;
}

export interface SeoFacets {
  cities: SeoFacet[];
  localities: SeoFacet[];
  industries: SeoFacet[];
  roles: SeoFacet[];
}

export interface SeoKeyword {
  query: string;
  clicks: number;
  impressions: number;
  /** A fraction, 0 to 1. */
  ctr: number;
  position: number | null;
  topPage: string | null;
  topPageClicks: number;
  pages: number;
  clicksPrev: number;
  positionPrev: number | null;
  clicksChange: number;
  /** Previous position minus current: positive means it moved up. */
  positionChange: number | null;
}

export interface SeoKeywordPage {
  content: SeoKeyword[];
  totalElements: number;
  totalPages: number;
  from: string;
  to: string;
}

export interface SeoDailyPoint {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
  sessions: number;
  signups: number;
}

export interface SeoQueryStat {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
}

export interface SeoChannels {
  organic: number;
  ai: number;
  direct: number;
  social: number;
  referral: number;
  other: number;
}

export interface SeoPagePerformance {
  path: string;
  from: string;
  to: string;
  daily: SeoDailyPoint[];
  topQueries: SeoQueryStat[];
  channels: SeoChannels;
  googleIndex: SeoGoogleIndex | null;
}

export const TYPE_LABEL: Record<string, string> = {
  'mumbai-hub': 'Mumbai hub',
  'mumbai-vertical-hub': 'Mumbai vertical hub',
  'mumbai-role': 'Mumbai role',
  'mumbai-locality': 'Mumbai area',
  'mumbai-directory': 'Mumbai directory',
  'national-landing': 'National role',
  taxonomy: 'Taxonomy',
  guide: 'Guide',
  compare: 'Compare',
  marketing: 'Marketing',
};

export const typeLabel = (t?: string | null) =>
  t ? (TYPE_LABEL[t] ?? t) : '-';

/** The chip status that colours each event kind: CRMStatusChip's palette, our words. */
export const EVENT_CHIP: Record<
  SeoEventKind,
  { status: string; label: string }
> = {
  ADDED: { status: 'new', label: 'Added' },
  CHANGED: { status: 'pending', label: 'Changed' },
  REMOVED: { status: 'closed', label: 'Removed' },
  NOTE: { status: 'approved', label: 'Note' },
};

/** A page path as a URL segment, and back. The path is the record's id. */
export const pageIdOf = (path: string) =>
  encodeURIComponent(path.replace(/^\//, ''));
export const pathOf = (id: string) => `/${decodeURIComponent(id)}`;

export const PAGE_SIZE = 50;

/** One field's change in words: "FAQ count changed from 4 to 6". */
export const changeText = (c: {
  field: string;
  from: string | null;
  to: string | null;
}) => `${c.field} changed from ${c.from ?? 'none'} to ${c.to ?? 'none'}`;

export const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/** The server's sort keys for the pages list; '' is its default, type then path. */
export const PAGE_SORTS = [
  '',
  'clicks',
  'impressions',
  'position',
  'sessions',
  'signups',
  'path',
] as const;
export type PageSort = (typeof PAGE_SORTS)[number];

export const PAGE_SORT_LABEL: Record<PageSort, string> = {
  '': 'Type, then page',
  clicks: 'Most clicks',
  impressions: 'Most impressions',
  position: 'Best position',
  sessions: 'Most visits',
  signups: 'Most sign-ups',
  path: 'Page, A to Z',
};

export interface PagesState {
  q: string;
  pageType: string;
  indexable: '' | 'yes' | 'no';
  llms: '' | 'yes' | 'no';
  /** Indexable by our decision, but Google's verdict is not PASS. */
  notIndexed: boolean;
  /** Clicks over 28 days at most half the previous 28. */
  dropping: boolean;
  city: string;
  locality: string;
  industry: string;
  role: string;
  sort: PageSort;
  page: number;
}

/** The four facet filters: their URL key, label, and where seoFacets lists their values. */
export const FACET_FILTERS = [
  { key: 'city', label: 'City', facets: 'cities' },
  { key: 'locality', label: 'Locality', facets: 'localities' },
  { key: 'industry', label: 'Industry', facets: 'industries' },
  { key: 'role', label: 'Role', facets: 'roles' },
] as const satisfies readonly { key: keyof PagesState; label: string; facets: keyof SeoFacets }[];

export const EMPTY_PAGES_STATE: PagesState = {
  q: '',
  pageType: '',
  indexable: '',
  llms: '',
  notIndexed: false,
  dropping: false,
  city: '',
  locality: '',
  industry: '',
  role: '',
  sort: '',
  page: 0,
};

const flag = (v: string) =>
  v === 'yes' ? true : v === 'no' ? false : undefined;

export const toFilter = (s: PagesState): SeoPageFilterInput => ({
  q: s.q.trim() || undefined,
  pageType: s.pageType || undefined,
  indexable: flag(s.indexable),
  inLlms: flag(s.llms),
  notIndexed: s.notIndexed || undefined,
  dropping: s.dropping || undefined,
  city: s.city || undefined,
  locality: s.locality || undefined,
  industry: s.industry || undefined,
  role: s.role || undefined,
});

/** The list's state lives in the URL, so a filtered view can be shared and survives a reload. */
export const parsePagesParams = (p: URLSearchParams): PagesState => ({
  q: p.get('q') ?? '',
  pageType: p.get('type') ?? '',
  indexable: (['yes', 'no'].includes(p.get('indexable') ?? '')
    ? p.get('indexable')
    : '') as PagesState['indexable'],
  llms: (['yes', 'no'].includes(p.get('llms') ?? '')
    ? p.get('llms')
    : '') as PagesState['llms'],
  notIndexed: p.get('notIndexed') === '1',
  dropping: p.get('dropping') === '1',
  city: p.get('city') ?? '',
  locality: p.get('locality') ?? '',
  industry: p.get('industry') ?? '',
  role: p.get('role') ?? '',
  sort: ((PAGE_SORTS as readonly string[]).includes(p.get('sort') ?? '')
    ? p.get('sort')
    : '') as PageSort,
  page: Math.max(0, Number(p.get('page')) || 0),
});

export const toPagesParams = (s: PagesState, tab: string): URLSearchParams => {
  const p = new URLSearchParams();
  if (tab !== 'pages') p.set('tab', tab);
  if (s.q) p.set('q', s.q);
  if (s.pageType) p.set('type', s.pageType);
  if (s.indexable) p.set('indexable', s.indexable);
  if (s.llms) p.set('llms', s.llms);
  if (s.notIndexed) p.set('notIndexed', '1');
  if (s.dropping) p.set('dropping', '1');
  for (const f of FACET_FILTERS) if (s[f.key]) p.set(f.key, s[f.key]);
  if (s.sort) p.set('sort', s.sort);
  if (s.page) p.set('page', String(s.page));
  return p;
};

/* ---------------------------------------------------------------- keywords */

export const KEYWORD_SORTS = [
  'clicks',
  'impressions',
  'ctr',
  'position',
  'change',
  'query',
] as const;
export type KeywordSort = (typeof KEYWORD_SORTS)[number];

export interface KeywordsState {
  q: string;
  minImpressions: number;
  sort: KeywordSort;
  page: number;
}

export const EMPTY_KEYWORDS_STATE: KeywordsState = {
  q: '',
  minImpressions: 0,
  sort: 'clicks',
  page: 0,
};

/*
 * The keywords tab's own keys, prefixed, so its search and page never collide with the pages
 * list's `q` and `page`: those keys are what a shared pages link is made of, and must not change.
 */
export const parseKeywordsParams = (p: URLSearchParams): KeywordsState => ({
  q: p.get('kq') ?? '',
  minImpressions: Math.max(0, Math.floor(Number(p.get('kmin')) || 0)),
  sort: ((KEYWORD_SORTS as readonly string[]).includes(p.get('ksort') ?? '')
    ? p.get('ksort')
    : 'clicks') as KeywordSort,
  page: Math.max(0, Number(p.get('kpage')) || 0),
});

export const toKeywordsParams = (s: KeywordsState): URLSearchParams => {
  const p = new URLSearchParams();
  p.set('tab', 'keywords');
  if (s.q) p.set('kq', s.q);
  if (s.minImpressions > 0) p.set('kmin', String(s.minImpressions));
  if (s.sort !== 'clicks') p.set('ksort', s.sort);
  if (s.page) p.set('kpage', String(s.page));
  return p;
};

export const toKeywordFilter = (s: KeywordsState): SeoKeywordFilterInput => ({
  q: s.q.trim() || undefined,
  minImpressions: s.minImpressions > 0 ? s.minImpressions : undefined,
});

/* ---------------------------------------------------------------- formatting */

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A Search Console day ("2026-09-12") as "12 Sep". Read as a calendar date, never through Date:
 * parsed as UTC midnight and shown in IST it is the same day, but anywhere west of UTC it is the
 * day before.
 */
export const dayLabel = (iso: string | null | undefined): string => {
  const m = DAY.exec(iso ?? '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : '-';
};

/** The window a figure covers: "12 Sep to 9 Oct". */
export const rangeLabel = (from?: string | null, to?: string | null) =>
  from && to ? `${dayLabel(from)} to ${dayLabel(to)}` : '';

export const fmtCount = (n: number | null | undefined) =>
  n === null || n === undefined ? '-' : n.toLocaleString('en-IN');

/** Average position to one decimal; lower is better, 1 is the top result. */
export const fmtPosition = (p: number | null | undefined) =>
  p === null || p === undefined ? '-' : p.toFixed(1);

/** CTR arrives as a fraction. */
export const fmtCtr = (c: number | null | undefined) =>
  c === null || c === undefined ? '-' : `${(c * 100).toFixed(1)}%`;

export type ChangeDir = 'up' | 'down' | 'flat' | 'new';

export interface Change {
  text: string;
  dir: ChangeDir;
}

/** A change in clicks: "+12", "-3", or "0". Up is good. */
export const clicksChange = (n: number): Change =>
  n > 0
    ? { text: `+${n.toLocaleString('en-IN')}`, dir: 'up' }
    : n < 0
      ? { text: `-${Math.abs(n).toLocaleString('en-IN')}`, dir: 'down' }
      : { text: '0', dir: 'flat' };

/**
 * A change in position, as the backend gives it: previous minus current, so positive means the
 * query moved up the results. Null when there was no previous position: it is new.
 */
export const positionChange = (n: number | null | undefined): Change => {
  if (n === null || n === undefined) return { text: 'New', dir: 'new' };
  const r = Math.round(n * 10) / 10;
  if (r > 0) return { text: `+${r.toFixed(1)}`, dir: 'up' };
  if (r < 0) return { text: `-${Math.abs(r).toFixed(1)}`, dir: 'down' };
  return { text: '0.0', dir: 'flat' };
};

/** The Google index chip: Indexed only on a PASS verdict. */
export const indexChip = (
  g: SeoGoogleIndex | null | undefined,
): { status: string; label: string } =>
  !g?.verdict
    ? { status: 'inactive', label: 'Not checked' }
    : g.verdict === 'PASS'
      ? { status: 'active', label: 'Indexed' }
      : { status: 'pending', label: 'Not indexed' };

/** "Ranks #4 for jewellery jobs" or "Not ranking yet for jewellery jobs". */
export const primaryKeywordCallout = (
  keyword: string | null | undefined,
  position: number | null | undefined,
): string | null => {
  if (!keyword) return null;
  return position === null || position === undefined
    ? `Not ranking yet for ${keyword}`
    : `Ranks #${Math.max(1, Math.round(position))} for ${keyword}`;
};

export const PERFORMANCE_DAYS = [28, 90, 480] as const;
export type PerformanceDays = (typeof PERFORMANCE_DAYS)[number];

export const fetchPages = async (s: PagesState) => {
  const r = await fetchGraphQLApiData(
    seoPagesQuery(toFilter(s), s.page, PAGE_SIZE, s.sort || undefined),
  );
  return r?.data?.seoPages as {
    totalElements: number;
    totalPages: number;
    content: SeoPage[];
    totals: SeoPageTotals | null;
  };
};

export const fetchFacets = async () => {
  const r = await fetchGraphQLApiData(seoFacetsQuery());
  return (r?.data?.seoFacets ?? null) as SeoFacets | null;
};

export const fetchSummary = async () => {
  const r = await fetchGraphQLApiData(seoSummaryQuery());
  return (r?.data?.seoSummary ?? null) as SeoSummary | null;
};

export const fetchEvents = async (kind: string, page: number) => {
  const r = await fetchGraphQLApiData(
    seoPageEventsQuery(kind, page, PAGE_SIZE),
  );
  return r?.data?.seoPageEvents as {
    totalElements: number;
    content: SeoEvent[];
  };
};

export const fetchPageRecord = async (path: string) => {
  const r = await fetchGraphQLApiData(seoPageQuery(path));
  return {
    page: (r?.data?.seoPage ?? null) as SeoPage | null,
    events: (r?.data?.seoPageEvents?.content ?? []) as SeoEvent[],
  };
};

export const fetchKeywords = async (s: KeywordsState) => {
  const r = await fetchGraphQLApiData(
    seoKeywordsQuery(toKeywordFilter(s), s.sort, s.page, PAGE_SIZE),
  );
  return (r?.data?.seoKeywords ?? null) as SeoKeywordPage | null;
};

/** Every LIVE page for a role, with keyword checks: the Categories tab. */
export const fetchRolePages = async (role: string) => {
  const r = await fetchGraphQLApiData(seoRolePagesQuery(role));
  return (r?.data?.seoPages?.content ?? []) as SeoPage[];
};

/** The Search Console queries one page appeared for (28 days), most clicks first. */
export const fetchPageQueries = async (path: string) => {
  const r = await fetchGraphQLApiData(seoKeywordsQuery({ path }, 'clicks', 0, 200));
  return (r?.data?.seoKeywords?.content ?? []) as SeoKeyword[];
};

/** One row of the keyword table: the live check, and Google's figures for that exact query. */
export interface KeywordRow extends SeoKeywordCheck {
  /** Search Console position for exactly this query on this page, or null if Google never showed it. */
  position: number | null;
  clicks: number | null;
  impressions: number | null;
}

/** Lowercase, punctuation and repeated spaces dropped: how a query and a keyword are compared. */
export const keywordKey = (k: string) =>
  k
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

/**
 * The keyword checks joined to Search Console on the exact query. Primary first, then the rest in
 * the tracker's order. A query Google reports in other words ("jobs for jewellery sales in mumbai")
 * is not matched: the column answers "does Google show us for this phrase", nothing looser.
 */
export const keywordRows = (
  checks: SeoKeywordCheck[] | undefined,
  queries: SeoKeyword[],
): KeywordRow[] => {
  const byQuery = new Map(queries.map((q) => [keywordKey(q.query), q]));
  return [...(checks ?? [])]
    .sort((a, b) => Number(b.primary) - Number(a.primary))
    .map((c) => {
      const q = byQuery.get(keywordKey(c.keyword));
      return {
        ...c,
        position: q?.position ?? null,
        clicks: q ? q.clicks : null,
        impressions: q ? q.impressions : null,
      };
    });
};

/** Where on the page a keyword is, in one word for the summary column. */
export const keywordPlacement = (
  c: SeoKeywordCheck,
): 'strong' | 'body' | 'missing' =>
  c.inTitle || c.inH1 || c.inDescription || c.inH2
    ? 'strong'
    : c.inBody
      ? 'body'
      : 'missing';

export const fetchPerformance = async (path: string, days: number) => {
  const r = await fetchGraphQLApiData(seoPagePerformanceQuery(path, days));
  return (r?.data?.seoPagePerformance ?? null) as SeoPagePerformance | null;
};

/** A sync that was running is not any more: the poll has an outcome to tell. */
export const syncJustFinished = (
  before: SeoSyncState | null | undefined,
  after: SeoSyncState | null | undefined,
): boolean => !!before?.running && !!after && !after.running;

/** The finished sync in a toast: its result, or why it failed. */
export const syncOutcome = (
  s: SeoSyncState,
  done: string,
): { message: string; tone: 'success' | 'warning' | 'error' } => {
  if (s.error) return { message: s.error, tone: 'error' };
  const message = s.result || done;
  // "could not be read" is how both syncs report a partial result.
  return { message, tone: /could not be read/i.test(message) ? 'warning' : 'success' };
};

/** "12 pages · 340 clicks · 9,200 impressions · 51 visits · 3 sign-ups" for the totals strip. */
export const totalsText = (t: SeoPageTotals, ga4Connected: boolean): string[] => {
  const n = (v: number, one: string) => `${fmtCount(v)} ${v === 1 ? one : `${one}s`}`;
  return [
    n(t.pages, 'page'),
    n(t.clicks28, 'click'),
    n(t.impressions28, 'impression'),
    ...(ga4Connected ? [n(t.sessions28, 'visit'), n(t.signups28, 'sign-up')] : []),
  ];
};

const CSV_HEADER = [
  'Path',
  'Type',
  'Primary keyword',
  'Secondary keywords',
  'Title',
  'Meta description',
  'H1',
  'FAQ questions',
  'Structured data',
  'In llms.txt',
  'In sitemap',
  'Indexable',
  'HTTP status',
  'Live jobs',
  'Last change',
  'Notes',
];

const yesNoCell = (v: boolean | null | undefined) =>
  v === null || v === undefined ? '' : v ? 'Yes' : 'No';

/** Every page matching the filter, as CSV, walked 200 at a time like the payout export. */
export const pagesCsv = async (s: PagesState): Promise<string> => {
  const rows: SeoPage[] = [];
  for (let page = 0; page < 50; page += 1) {
    const r = await fetchGraphQLApiData(seoPagesQuery(toFilter(s), page, 200));
    const part = r?.data?.seoPages;
    rows.push(...((part?.content ?? []) as SeoPage[]));
    if (!part || page + 1 >= (part.totalPages ?? 0)) break;
  }
  const lines = [
    CSV_HEADER,
    ...rows.map((p) => [
      p.path,
      typeLabel(p.pageType),
      p.primaryKeyword,
      p.secondaryKeywords.join('; '),
      p.title,
      p.description,
      p.h1,
      p.faqCount,
      p.schemaTypes.join('; '),
      yesNoCell(p.inLlms),
      yesNoCell(p.inSitemap),
      yesNoCell(p.indexable),
      p.httpStatus,
      p.liveJobs,
      p.lastChangedAt,
      p.noteCount,
    ]),
  ];
  return lines.map((l) => l.map(csvCell).join(',')).join('\r\n');
};

const KEYWORD_CSV_HEADER = [
  'Query',
  'Top page',
  'Clicks',
  'Impressions',
  'CTR',
  'Average position',
  'Clicks, previous 28 days',
  'Clicks change',
  'Position change',
  'Pages showing',
];

/** Every keyword matching the filter, in the list's order, walked 200 at a time. */
export const keywordsCsv = async (s: KeywordsState): Promise<string> => {
  const rows: SeoKeyword[] = [];
  for (let page = 0; page < 50; page += 1) {
    const r = await fetchGraphQLApiData(
      seoKeywordsQuery(toKeywordFilter(s), s.sort, page, 200),
    );
    const part = r?.data?.seoKeywords;
    rows.push(...((part?.content ?? []) as SeoKeyword[]));
    if (!part || page + 1 >= (part.totalPages ?? 0)) break;
  }
  return keywordRowsCsv(rows);
};

export const keywordRowsCsv = (rows: SeoKeyword[]): string =>
  [
    KEYWORD_CSV_HEADER,
    ...rows.map((k) => [
      k.query,
      k.topPage,
      k.clicks,
      k.impressions,
      fmtCtr(k.ctr),
      k.position === null ? '' : fmtPosition(k.position),
      k.clicksPrev,
      k.clicksChange,
      k.positionChange === null ? '' : k.positionChange,
      k.pages,
    ]),
  ]
    .map((l) => l.map(csvCell).join(','))
    .join('\r\n');
